import React, { Component } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import classnames from 'classnames'
import omit from 'lodash/omit'
import forOwn from 'lodash/forOwn'
import startCase from 'lodash/startCase'
import capitalize from 'lodash/capitalize'
import uniq from 'lodash/uniq'
import { History } from 'history'

import { fetchTasks, assignMultipleTasks } from './taskActions'
import { selectAdminUserIndex } from '../../reducers/adminUsers'
import { fetchAdminList } from '../admin/adminActions'
import { nextPage, previousPage } from '../../helpers/paginationHelper'
import { selectTaskIndex, selectTaskMeta } from '../../reducers/tasks'
import {
  parseQuery,
  stringifyQuery,
  toggleSort,
} from '../../helpers/queryHelper'
import { utcToLocalDate } from '../../helpers/timeHelper'
import { TaskAdminDropdown } from './taskAdminDropdown'
import IndexHeaderRow from '../common/indexes/indexHeaderRow'

import { Meta, Task } from './types'
import { User } from '../../core/types'

export type Callback<T = void> = (...args: any) => T

export interface Props {
  adminUsers: User[]
  assignMultipleTasks: Callback
  fetchAdminList: Callback
  fetchTasks: Callback
  currentTasks: Task[]
  history: History
  meta: Meta
  location: { key: any } & Location // TODO-TS
}

export interface State {
  selectAll: boolean
  selectedTaskTypes: string[]
  taskIds: string[]
}

export class TaskIndex extends Component<Props> {
  static displayName = 'TaskIndex'

  state = { taskIds: [], selectAll: false, selectedTaskTypes: [] } as State

  componentDidMount() {
    const { fetchTasks, location, history } = this.props
    if (!location.search) {
      history.push({
        pathname: '/tasks',
        search: stringifyQuery({
          offset: 0,
          limit: 25,
          sort: 'PENDING_AT',
          direction: 'ASC',
          status: ['PENDING'],
        }),
      })
    } else {
      if (
        location.search === '?offset=0&limit=25?success=true&message=Success!'
      ) {
        fetchTasks(parseQuery('?offset=0&limit=25'))
      } else {
        const parsedQuery = parseQuery(location.search)
        fetchTasks(parsedQuery)
      }
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      fetchAdminList,
      fetchTasks,
      location: { search },
    } = this.props
    if (search !== prevProps.location.search) {
      const parsedQuery = parseQuery(search)
      fetchTasks(parsedQuery)
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ taskIds: [], selectAll: false, selectedTaskTypes: [] })
    }
    if (prevState.selectedTaskTypes !== this.state.selectedTaskTypes) {
      fetchAdminList(this.state.selectedTaskTypes)
    }
  }

  prevPageClick = () => {
    const {
      location: { search },
    } = this.props
    previousPage(search, this.props.history, '/tasks')
  }

  nextPageClick = () => {
    const {
      currentTasks,
      location: { search },
    } = this.props
    nextPage(search, this.props.history, currentTasks.length, '/tasks')
  }

  filtersClick = () => {
    const {
      location: { search },
    } = this.props
    this.props.history.push({
      pathname: '/tasks/filters',
      search,
    })
  }

  headerClick = (sortEnum) => {
    const {
      location: { search },
    } = this.props
    this.props.history.push({
      pathname: '/tasks',
      search: toggleSort(search, sortEnum, 'ASC'),
    })
  }

  countFilters = (query, defaultFilters) => {
    let count = 0

    forOwn(omit(query, defaultFilters), (obj: any[] | any) => {
      if (Array.isArray(obj)) {
        count += obj.length
      } else {
        count += 1
      }
    })

    return count
  }

  filterBadge = () => {
    const {
      location: { search },
    } = this.props
    const parsedQuery = parseQuery(
      search !== '?offset=0&limit=25?success=true&message=Success!'
        ? search
        : '?offset=0&limit=25'
    )
    const ignoreKeys = ['offset', 'limit']
    const count = this.countFilters(parsedQuery, ignoreKeys)
    if (count > 0 && count < 10) {
      return <span className="index-table__filter-badge">{count}</span>
    } else if (count >= 10) {
      return (
        <span className="index-table__filter-badge index-table__filter-badge--multi">
          {count}
        </span>
      )
    } else {
      return <span className="index-table__filter-icon icon-filters" />
    }
  }

  openPage = (task: Task) => {
    //TEMP conditional until underwriting is removed from admin web
    let win: Window | null = window
    if (task.taskType === 'REVIEW_STATEMENT') {
      win = window.open(
        `
      ${process.env.REACT_APP_ADMIN_WEB_URL}underwriting/credit_report/${task.entityId}
      `,
        '_blank'
      )
      win && win.focus()
    } else if (task.taskType === 'REVIEW_OFAC') {
      win = window.open(
        `
      ${process.env.REACT_APP_ADMIN_WEB_URL}underwriting/${task.entityId}
      `,
        '_blank'
      )
      win && win.focus()
    } else {
      this.props.history.push(`tasks/${task.id}/edit`)
    }
  }

  updateSelectedTasks = (event, id: string, taskType: string) => {
    event.stopPropagation()
    this.setState({ selectAll: false })
    const { taskIds, selectedTaskTypes } = this.state
    const { fetchAdminList } = this.props

    if (!taskIds.includes(id)) {
      taskIds.push(id)
      selectedTaskTypes.push(taskType)
      this.setState({ taskIds, selectedTaskTypes })
      fetchAdminList(selectedTaskTypes)
    } else {
      const filteredTaskIds = taskIds.filter((item) => !item.includes(id))
      const filteredTaskTypes = selectedTaskTypes.filter(
        (item) => !item.includes(taskType)
      )
      this.setState({
        taskIds: filteredTaskIds,
        selectedTaskTypes: filteredTaskTypes,
      })
    }
  }

  selectAllTasks = () => {
    const { fetchAdminList } = this.props
    const { taskIds, selectedTaskTypes } = this.state
    this.setState({ selectAll: true })

    if (taskIds.length === 0) {
      this.props.currentTasks.map(({ id, taskType }) => {
        taskIds.push(id)
        selectedTaskTypes.push(taskType)
      })
      this.setState({ taskIds, selectedTaskTypes: uniq(selectedTaskTypes) })
      fetchAdminList(selectedTaskTypes)
    } else {
      this.setState({ taskIds: [], selectAll: false, selectedTaskTypes: [] })
    }
  }

  reassignMultipleTasks = (adminId) => {
    const formatAssigneeId = adminId === 'null' ? null : adminId
    this.props.assignMultipleTasks(this.state.taskIds, formatAssigneeId)
    this.setState({ taskIds: [], selectAll: false, selectedTaskTypes: [] })
  }

  render() {
    const { adminUsers, currentTasks, meta, location } = this.props
    const { total } = meta
    const { search } = location
    const parsedSearch = parseQuery(search)
    const headerList = [
      { displayHeader: 'NAME (Last, First)', sortable: false },
      { displayHeader: 'STATE', sortable: false },
      { displayHeader: 'TASK', sortable: false },
      { displayHeader: 'STATUS', sortable: false },
      { displayHeader: 'DATE', sortable: true, sortEnum: 'PENDING_AT' },
      { displayHeader: 'ASSIGNEE', sortable: false },
      { displayHeader: 'CATEGORY', sortable: false },
    ]

    const rows = currentTasks.map((task, index) => {
      const { assignee, pendingAt, status, taskType, user, entityType, id } =
        task
      return (
        <tr
          key={index}
          className="index-table__row index-table__row--batch-row"
          onClick={() => this.openPage(task)}
        >
          <td className="index-table__cell">
            <input
              type="checkbox"
              id="task-checkbox"
              className="index-table__checkbox"
              value={id}
              checked={this.state.taskIds.includes(id)}
              onClick={(event) => this.updateSelectedTasks(event, id, taskType)}
            />
          </td>
          {user ? (
            <td className="index-table__cell">
              {`${user.lastName}, ${user.firstName}`}
            </td>
          ) : (
            <td />
          )}
          {user && user.activationState ? (
            <td className="index-table__cell">{user.activationState}</td>
          ) : (
            <td />
          )}
          {taskType ? (
            <td className="index-table__cell">
              {startCase(capitalize(taskType))}
            </td>
          ) : (
            <td />
          )}
          {status ? <td className="index-table__cell">{status}</td> : <td />}
          {pendingAt ? (
            <td className="index-table__cell">{utcToLocalDate(pendingAt)}</td>
          ) : (
            <td />
          )}
          {assignee ? (
            <td className="index-table__cell">{assignee.name}</td>
          ) : (
            <td />
          )}
          {entityType ? (
            <td className="index-table__cell">{entityType}</td>
          ) : (
            <td />
          )}
        </tr>
      )
    })

    return (
      <div className="index" key={location.key}>
        <h1 className="index-table__header">
          Task Index
          <span className="index-table__header--total">{total}</span>
        </h1>
        <div className="index-table__controls">
          <TaskAdminDropdown
            adminUsers={adminUsers}
            reassignTask={this.reassignMultipleTasks}
            indexStyle
            disabled={this.state.taskIds.length === 0}
            totalTasks={this.state.taskIds.length}
          />
          <a
            data-testid="FiltersLink"
            onClick={this.filtersClick}
            className="index-table__filter-button index-table__filter-button--tasks"
          >
            {this.filterBadge()}
            filters
          </a>
        </div>
        <table className="index-table">
          <IndexHeaderRow
            headerList={headerList}
            parsedSearch={parsedSearch}
            clickCallback={this.headerClick}
            batch={{
              batchState: this.state.selectAll,
              batchCallback: () => this.selectAllTasks(),
            }}
          />
          <tbody className="index-table__body">{rows}</tbody>
        </table>
        <div className="index-table__button-row">
          <a
            data-testid="previousPageLink"
            onClick={this.prevPageClick}
            className={classnames(
              'index-table__button',
              'index-table__button--previous',
              'icon-arrow',
              {
                'index-table__button--disabled':
                  parsedSearch.limit > parsedSearch.offset,
              }
            )}
          />
          <a
            data-testid="nextPageLink"
            onClick={this.nextPageClick}
            className={classnames(
              'index-table__button',
              'index-table__button--next',
              'icon-arrow',
              {
                'index-table__button--disabled':
                  currentTasks.length < parsedSearch.limit,
              }
            )}
          />
        </div>
      </div>
    )
  }
}

export const mapStateToProps = (state) => {
  return {
    adminUsers: selectAdminUserIndex(state.adminUsers),
    currentTasks: selectTaskIndex(state.tasks),
    meta: selectTaskMeta(state.tasks),
  }
}

export default withRouter(
  connect(mapStateToProps, {
    assignMultipleTasks,
    fetchAdminList,
    fetchTasks,
  })(TaskIndex)
)
